import api from 'api';
import {usePostApi} from 'common/hooks/usePostApi';
import React from 'react';
import {ILogBuffer} from 'types/diplomat/logs/LogBuffer';
import {ILogView} from 'types/diplomat/logs/LogView';
import makeStyles from '@mui/styles/makeStyles';
import {Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {FixedSizeList as List} from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';
import {debounce} from 'lodash';
import Panel from 'components/Panel';

const useStyles = makeStyles(() => ({
   root: {
      height: '30rem',
   },
   list: {},
}));

type Props = {
   logView: ILogView;
   isCloud?: boolean;
};
export const LogText = React.memo(({logView, isCloud}: Props) => {
   const classes = useStyles();
   const {t} = useTranslation();
   const {viewId, numLines} = logView;

   const options = React.useMemo(
      () => ({
         persistState: true,
      }),
      []
   );
   const {request, state: logBuffer} = usePostApi<ILogBuffer>(
      isCloud ? api.remoteAgentLogsText : api.logsText,
      options
   );
   React.useEffect(() => {
      request(
         JSON.stringify({
            viewId,
            lineNumber: 0,
         })
      );
   }, [viewId, request]);
   const loadBuffer = React.useCallback(debounce(request, 500), [request]);

   const loadMoreItems = React.useCallback(
      (startIndex: number) => {
         // console.debug('loadMoreItems', startIndex, stopIndex);
         if (viewId) {
            return loadBuffer(
               JSON.stringify({
                  viewId,
                  lineNumber: startIndex,
               })
            )!;
         }
         return null;
      },
      [loadBuffer, viewId]
   );

   const isItemLoaded = React.useCallback(
      (i) => {
         // console.debug('isItemLoaded', i);
         return !!(
            logBuffer &&
            i >= logBuffer?.lineNumber! &&
            i < logBuffer?.lineNumber! + logBuffer?.buffer!.length
         );
      },
      [logBuffer]
   );

   return (
      <Panel className={classes.root}>
         {/* <div>{JSON.stringify(state)}</div> */}
         {logBuffer ? (
            <InfiniteLoader
               isItemLoaded={isItemLoaded}
               itemCount={numLines}
               loadMoreItems={loadMoreItems}
            >
               {({onItemsRendered, ref}) => (
                  <List
                     className="List"
                     height={340}
                     itemCount={numLines}
                     itemSize={20}
                     onItemsRendered={onItemsRendered}
                     ref={ref}
                     width="100%"
                  >
                     {({index, style}) => (
                        <Typography style={{...style, whiteSpace: 'nowrap'}}>
                           {logBuffer.buffer[index - logBuffer.lineNumber]}
                        </Typography>
                     )}
                  </List>
               )}
            </InfiniteLoader>
         ) : (
            <Typography>{t('Log is empty')}</Typography>
         )}
      </Panel>
   );
});
